import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminAnalyticsNav from '@components/admin/AdminAnalyticsNav';
import AdminDrilldownModal from '@components/admin/AdminDrilldownModal';
import {
	getAdminActivityOverview,
	getAdminActivityDrilldown,
	type ActivityDrilldownResponse,
	type ActivityOverviewResponse
} from '@services/adminActivity';
import { useSearchHighlight } from '../../shared/useSearchHighlight';

const PAGE_OPTIONS = [
	{ value: '/', label: 'Homepage (hortera.org)' },
	{ value: '/app/home', label: 'Home (logged-in app)' },
	{ value: '/gallery', label: 'Gallery' },
	{ value: '/resources', label: 'Resources' },
	{ value: '/calculator', label: 'Savings calculator' },
	{ value: '/garden-seeker', label: 'Seeker landing' },
	{ value: '/login', label: 'Login' },
	{ value: '/try-questionnaire', label: 'Try questionnaire' },
	{ value: '/verify-email', label: 'Email verification' },
	{ value: '/app/seeker/create-plan', label: 'Create plan' },
	{ value: '/app/seeker/generate-plan', label: 'Generate plan' }
];

const GITEX_START = '2026-06-30';

function todayIsoDate(): string {
	return new Date().toISOString().slice(0, 10);
}

function formatDate(value: string): string {
	return new Date(value).toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'short'
	});
}

function formatDateRange(start: string | null, end: string): string {
	if (!start) return `All time – ${formatDate(end)}`;
	return `${formatDate(start)} - ${formatDate(end)}`;
}

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString('en-GB', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
}

function percent(numerator: number, denominator: number): string {
	if (denominator <= 0) return '0%';
	return `${Math.round((numerator / denominator) * 100)}%`;
}

function maxOrOne(values: number[]): number {
	const max = Math.max(...values, 0);
	return max > 0 ? max : 1;
}

export default function AdminEngagement() {
	const [overview, setOverview] = useState<ActivityOverviewResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [mode, setMode] = useState<'30d' | '90d' | 'gitex' | 'custom'>('30d');
	const [pagePath, setPagePath] = useState('/');
	const [customStart, setCustomStart] = useState(GITEX_START);
	const [customEnd, setCustomEnd] = useState(todayIsoDate());
	const [eventSearch, setEventSearch] = useState('');
	const [pageSearch, setPageSearch] = useState('');
	const [drilldown, setDrilldown] = useState<ActivityDrilldownResponse | null>(null);
	const [drilldownLoading, setDrilldownLoading] = useState(false);
	const [drilldownError, setDrilldownError] = useState<string | null>(null);
	useSearchHighlight();

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		const request =
			mode === 'custom'
				? { startDate: customStart, endDate: customEnd, pagePath }
				: mode === 'gitex'
					? { startDate: GITEX_START, endDate: todayIsoDate(), pagePath }
					: { days: mode === '90d' ? 90 : 30, pagePath };

		getAdminActivityOverview(request)
			.then((data) => {
				if (!cancelled) setOverview(data);
			})
			.catch((err) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : 'Failed to load engagement data');
					setOverview(null);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [mode, pagePath, customStart, customEnd]);

	const metrics = overview?.metrics;
	const dailyRows = overview?.daily ?? [];
	const eventRows = useMemo(() => {
		const q = eventSearch.trim().toLowerCase();
		return (overview?.event_breakdown ?? []).filter((row) =>
			q ? row.event_name.toLowerCase().includes(q) : true
		);
	}, [overview?.event_breakdown, eventSearch]);
	const pageRows = useMemo(() => {
		const q = pageSearch.trim().toLowerCase();
		return (overview?.page_breakdown ?? []).filter((row) =>
			q ? row.page_key.toLowerCase().includes(q) : true
		);
	}, [overview?.page_breakdown, pageSearch]);
	const gitexRows = useMemo(
		() => dailyRows.filter((row) => row.day >= GITEX_START),
		[dailyRows]
	);
	const gitexSummary = useMemo(
		() =>
			gitexRows.reduce(
				(acc, row) => {
					acc.newUsers += row.new_users;
					acc.visitors += row.unique_visitors;
					acc.sessions += row.sessions;
					return acc;
				},
				{ newUsers: 0, visitors: 0, sessions: 0 }
			),
		[gitexRows]
	);
	const maxDailyVisitors = maxOrOne(dailyRows.map((row) => row.logged_in_visitors + row.anonymous_visitors));
	const selectedPageLabel =
		PAGE_OPTIONS.find((option) => option.value === pagePath)?.label ?? pagePath;

	const openVisitorDrilldown = (day: string) => {
		setDrilldownLoading(true);
		setDrilldownError(null);
		setDrilldown(null);
		getAdminActivityDrilldown({ kind: 'page_visitors', day, pagePath })
			.then((data) => setDrilldown(data))
			.catch((err) => {
				setDrilldownError(err instanceof Error ? err.message : 'Failed to load visitor details');
			})
			.finally(() => setDrilldownLoading(false));
	};

	const closeDrilldown = () => {
		setDrilldown(null);
		setDrilldownError(null);
		setDrilldownLoading(false);
	};

	return (
		<>
			<AdminDrilldownModal
				open={drilldownLoading || drilldown !== null || !!drilldownError}
				title="Visitors on selected day"
				subtitle={
					drilldown?.kind === 'page_visitors'
						? `${formatDate(drilldown.day)} · ${selectedPageLabel}`
						: selectedPageLabel
				}
				loading={drilldownLoading}
				error={drilldownError}
				onClose={closeDrilldown}
			>
				{drilldown?.kind === 'page_visitors' ? (
					<div className="table-responsive">
						<table className="table table-sm align-middle mb-0">
							<thead className="text-muted small text-uppercase">
								<tr>
									<th>Visitor</th>
									<th>Type</th>
									<th className="text-end">Views</th>
									<th className="text-end">Sessions</th>
									<th className="text-end">Last seen</th>
								</tr>
							</thead>
							<tbody>
								{drilldown.visitors.map((visitor) => (
									<tr key={visitor.identity_key}>
										<td>
											<div className="fw-semibold">
												{visitor.name || visitor.email || 'Anonymous visitor'}
											</div>
											<div className="small text-muted">
												{visitor.email || visitor.identity_key}
												{visitor.company_name ? ` · ${visitor.company_name}` : ''}
											</div>
										</td>
										<td>
											{visitor.is_logged_in ? (
												<>
													<div className="text-capitalize">{visitor.role || 'user'}</div>
													<div className="small text-muted">Logged in</div>
												</>
											) : (
												<>
													<div>Anonymous</div>
													<div className="small text-muted">No account match</div>
												</>
											)}
										</td>
										<td className="text-end">{visitor.view_count}</td>
										<td className="text-end">{visitor.session_count}</td>
										<td className="text-end">{formatDateTime(visitor.last_seen_at)}</td>
									</tr>
								))}
								{!drilldown.visitors.length && (
									<tr>
										<td colSpan={5} className="text-muted small">
											No visitor details for that day.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				) : null}
			</AdminDrilldownModal>
			<div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-3">
				<div>
					<h1 className="h3 mb-1">Engagement</h1>
					<p className="text-muted small mb-0">
						Traffic, visitor mix, registrations, and funnel movement over time.{' '}
						<Link to="/app/dashboard" className="text-decoration-none" style={{ color: '#2f9e59' }}>
							Operational dashboard
						</Link>
					</p>
				</div>
				<div className="text-muted small">
					{overview ? formatDateRange(overview.window_start_date, overview.window_end_date) : 'Loading…'}
				</div>
			</div>

			<AdminAnalyticsNav />

			<div className="card mb-4">
				<div className="card-body">
					<div className="row g-3 align-items-end">
						<div className="col-12 col-lg-4">
							<label className="form-label small text-muted mb-1">Page focus</label>
							<select
								className="form-select"
								value={pagePath}
								onChange={(e) => setPagePath(e.target.value)}
							>
								{PAGE_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
						<div className="col-12 col-lg-8">
							<div className="d-flex flex-wrap gap-2">
								<button type="button" className={`btn btn-sm ${mode === '30d' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setMode('30d')}>
									Last 30 days
								</button>
								<button type="button" className={`btn btn-sm ${mode === '90d' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setMode('90d')}>
									Last 90 days
								</button>
								<button type="button" className={`btn btn-sm ${mode === 'gitex' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setMode('gitex')}>
									Since Gitex
								</button>
								<button type="button" className={`btn btn-sm ${mode === 'custom' ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => setMode('custom')}>
									Custom range
								</button>
							</div>
							{mode === 'custom' && (
								<div className="row g-2 mt-2">
									<div className="col-6 col-md-4">
										<label className="form-label small text-muted mb-1">Start</label>
										<input type="date" className="form-control" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
									</div>
									<div className="col-6 col-md-4">
										<label className="form-label small text-muted mb-1">End</label>
										<input type="date" className="form-control" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
									</div>
								</div>
							)}
						</div>
					</div>
					{error && <div className="alert alert-warning mt-3 mb-0 py-2 small">{error}</div>}
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-success-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">{selectedPageLabel} page views</div>
							<div className="display-6 my-1">{loading ? '…' : metrics?.page_views ?? metrics?.unique_visitors ?? 0}</div>
							<div className="text-muted small">
								{metrics?.logged_in_visitors ?? 0} logged in · {metrics?.anonymous_visitors ?? 0} anonymous
							</div>
						</div>
					</div>
				</div>
				<div className="col-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-primary-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">New users</div>
							<div className="display-6 my-1">{loading ? '…' : metrics?.new_users ?? 0}</div>
							<div className="text-muted small">Accounts created in this window</div>
						</div>
					</div>
				</div>
				<div className="col-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-warning-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Sessions</div>
							<div className="display-6 my-1">{loading ? '…' : metrics?.total_sessions ?? 0}</div>
							<div className="text-muted small">{metrics?.total_events ?? 0} tracked events</div>
						</div>
					</div>
				</div>
				<div className="col-6 col-xl-3">
					<div className="card h-100 border-top border-4 border-info-subtle">
						<div className="card-body">
							<div className="text-muted small text-uppercase">Questionnaire completion</div>
							<div className="display-6 my-1">
								{loading ? '…' : percent(metrics?.questionnaire_submits ?? 0, metrics?.questionnaire_starts ?? 0)}
							</div>
							<div className="text-muted small">
								{metrics?.questionnaire_submits ?? 0} of {metrics?.questionnaire_starts ?? 0} starts
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-12 col-xl-8">
					<div className="card h-100">
						<div className="card-header">Daily traffic mix</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Date</th>
											<th className="text-end">Visitors</th>
											<th>Logged in vs anonymous</th>
											<th className="text-end">New users</th>
											<th className="text-end">Sessions</th>
										</tr>
									</thead>
									<tbody>
										{dailyRows.map((row) => {
											const total = row.logged_in_visitors + row.anonymous_visitors;
											return (
												<tr
													key={row.day}
													onClick={() => openVisitorDrilldown(row.day)}
													style={{ cursor: 'pointer' }}
												>
													<td>
														<div className="fw-semibold">{formatDate(row.day)}</div>
														<div className="small text-muted">Click for visitor list</div>
													</td>
													<td className="text-end fw-semibold">{row.unique_visitors}</td>
													<td style={{ minWidth: 220 }}>
														<div className="progress" style={{ height: '0.65rem' }}>
															<div className="progress-bar bg-success" style={{ width: `${(row.logged_in_visitors / maxDailyVisitors) * 100}%` }} />
															<div className="progress-bar bg-secondary" style={{ width: `${(row.anonymous_visitors / maxDailyVisitors) * 100}%` }} />
														</div>
														<div className="small text-muted mt-1">
															{row.logged_in_visitors} logged in · {row.anonymous_visitors} anonymous
															{total > 0 ? ` (${percent(row.logged_in_visitors, total)} known)` : ''}
														</div>
													</td>
													<td className="text-end">{row.new_users}</td>
													<td className="text-end">{row.sessions}</td>
												</tr>
											);
										})}
										{!dailyRows.length && (
											<tr>
												<td colSpan={5} className="text-muted small">No activity yet for this range.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-4">
					<div className="card h-100">
						<div className="card-header">Gitex watch</div>
						<div className="card-body">
							<div className="text-muted small mb-3">
								Quick look at activity from <strong>30 Jun 2026</strong> onward.
							</div>
							<div className="border rounded p-3 mb-2">
								<div className="text-muted small">New users since Gitex</div>
								<div className="fs-3 fw-semibold">{gitexSummary.newUsers}</div>
							</div>
							<div className="border rounded p-3 mb-2">
								<div className="text-muted small">Visitors since Gitex</div>
								<div className="fs-3 fw-semibold">{gitexSummary.visitors}</div>
							</div>
							<div className="border rounded p-3">
								<div className="text-muted small">Sessions since Gitex</div>
								<div className="fs-3 fw-semibold">{gitexSummary.sessions}</div>
							</div>
							<button type="button" className="btn btn-outline-success btn-sm mt-3" onClick={() => setMode('gitex')}>
								Open Gitex range
							</button>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
							<span>Page breakdown</span>
							<input
								type="search"
								className="form-control form-control-sm"
								placeholder="Search pages"
								value={pageSearch}
								onChange={(e) => setPageSearch(e.target.value)}
								style={{ width: 220 }}
							/>
						</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Page</th>
											<th className="text-end">Views</th>
											<th className="text-end">Visitors</th>
											<th className="text-end">Sessions</th>
										</tr>
									</thead>
									<tbody>
										{pageRows.slice(0, 12).map((row) => (
											<tr key={row.page_key}>
												<td>{row.page_key}</td>
												<td className="text-end">{row.views}</td>
												<td className="text-end">{row.unique_visitors}</td>
												<td className="text-end">{row.sessions}</td>
											</tr>
										))}
										{!pageRows.length && (
											<tr>
												<td colSpan={4} className="text-muted small">No matching pages.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-12 col-xl-6">
					<div className="card h-100">
						<div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
							<span>Event breakdown</span>
							<input
								type="search"
								className="form-control form-control-sm"
								placeholder="Search events"
								value={eventSearch}
								onChange={(e) => setEventSearch(e.target.value)}
								style={{ width: 220 }}
							/>
						</div>
						<div className="card-body">
							<div className="table-responsive">
								<table className="table table-sm align-middle mb-0">
									<thead className="text-muted small text-uppercase">
										<tr>
											<th>Event</th>
											<th className="text-end">Events</th>
											<th className="text-end">Sessions</th>
											<th className="text-end">Avg sec</th>
										</tr>
									</thead>
									<tbody>
										{eventRows.slice(0, 12).map((row) => (
											<tr key={row.event_name}>
												<td>{row.event_name}</td>
												<td className="text-end">{row.events}</td>
												<td className="text-end">{row.sessions}</td>
												<td className="text-end">{Math.round(row.avg_duration_seconds ?? 0)}</td>
											</tr>
										))}
										{!eventRows.length && (
											<tr>
												<td colSpan={4} className="text-muted small">No matching events.</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
